/**
 * Clipboard platform adapter (src/platform, Story 5.2). A defensive, per-call write
 * over `navigator.clipboard.writeText`, in the same feature-detection style as
 * `capabilities.ts`: every path is guarded, and the API being absent, blocked by
 * policy, or throwing/rejecting yields `false` (never a throw across the UI boundary,
 * AD-7). Detection is PER CALL, not a cached `Capabilities` snapshot, because a
 * clipboard permission can change between load and click, so a stale snapshot would
 * be wrong. The clipboard write is an ENHANCEMENT; core use never requires it (the
 * ui reveals the selectable-field fallback when this returns `false`).
 */

/**
 * Copy `text` to the system clipboard. Resolves `true` on a successful write, `false`
 * on any absence/denial/failure. NEVER throws and NEVER prompts for a permission that
 * would block core use.
 */
export async function copyText(text: string): Promise<boolean> {
  // Test seam (mirrors `__KEPLER_FORCE_NO_WEBGL__`): force the fallback path
  // deterministically in e2e regardless of the headless browser's clipboard support.
  // Never affects production (the flag is unset).
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { __KEPLER_FORCE_NO_CLIPBOARD__?: boolean }).__KEPLER_FORCE_NO_CLIPBOARD__ ===
      true
  ) {
    return false;
  }
  try {
    const clipboard =
      typeof navigator !== 'undefined'
        ? (navigator as Navigator & { clipboard?: Clipboard }).clipboard
        : undefined;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      return false;
    }
    await clipboard.writeText(text);
    return true;
  } catch {
    // Denied, blocked by policy, non-secure context, or a rejected write: fall back.
    return false;
  }
}
